import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Button, Form, Col, Row, Tabs, Tab, Table } from 'react-bootstrap';
import { Card } from 'ktm-ui';
import axios, { AxiosError } from 'axios';
import toastr from 'toastr';
import { Eye, EyeOff, ArrowDown, ArrowUp } from 'lucide-react';
import '../styles/Advisors.css';

const SENSITIVE_FIELDS = new Set(['BankTransitNo', 'BankNo', 'BankAccountNo']);

const MaskedValue = ({ value }: { value: string | null }) => {
    const [visible, setVisible] = useState(false);
    if (value == null || value === '') return <span style={{ color: '#9ca3af' }}>—</span>;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'monospace' }}>
            {visible ? value : value.replace(/\d/g, '•')}
            <button type="button" onClick={() => setVisible(v => !v)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 2 }} aria-label={visible ? 'Hide value' : 'Show value'}>
                {visible ? <EyeOff size={14} color="#6b7280" /> : <Eye size={14} color="#6b7280" />}
            </button>
        </span>
    );
};

const ChangeCell = ({ oldValue, newValue, sensitive }: { oldValue: string | null; newValue: string | null; sensitive: boolean }) => {
    const empty = <span style={{ color: '#9ca3af' }}>—</span>;
    const renderVal = (v: string | null) => (v == null || v === '') ? empty : (sensitive ? <MaskedValue value={v} /> : <span>{v}</span>);
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
            <span style={{ color: '#9ca3af', textDecoration: oldValue ? 'line-through' : 'none' }}>{renderVal(oldValue)}</span>
            <span style={{ color: '#9ca3af' }}>→</span>
            <span style={{ color: '#111827', fontWeight: 500 }}>{renderVal(newValue)}</span>
        </span>
    );
};

const DistroAuditLogTab = ({ distroAccountId, active }: { distroAccountId: string; active: boolean }) => {
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortDesc, setSortDesc] = useState(true);

    useEffect(() => {
        if (!active || !distroAccountId) return;
        setLoading(true);
        setError(null);
        axios.get(`/api/Advisors/distro/${distroAccountId}/auditlog`)
            .then(res => { setRows(res.data || []); })
            .catch(() => setError("Failed to load audit history."))
            .finally(() => setLoading(false));
    }, [active, distroAccountId]);

    const sortedRows = useMemo(() => {
        const copy = [...rows];
        copy.sort((a, b) => {
            const ta = new Date(a.changedAt).getTime();
            const tb = new Date(b.changedAt).getTime();
            return sortDesc ? tb - ta : ta - tb;
        });
        return copy;
    }, [rows, sortDesc]);

    if (loading && rows.length === 0) return <div style={{ padding: 16, color: '#6b7280' }}>Loading history…</div>;
    if (error) return <div style={{ padding: 16, color: '#b91c1c' }}>{error}</div>;
    if (rows.length === 0) return <div style={{ padding: 16, color: '#6b7280' }}>No edits recorded yet.</div>;

    return (
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <Table hover size="sm" style={{ marginBottom: 0 }}>
                <thead>
                    <tr>
                        {['Source', 'Field', 'Change'].map(h => (
                            <th key={h} style={{ whiteSpace: 'nowrap', borderBottom: '2px solid #d1d5db', paddingTop: 10, paddingBottom: 10 }}>{h}</th>
                        ))}
                        <th
                            style={{ whiteSpace: 'nowrap', borderBottom: '2px solid #d1d5db', paddingTop: 10, paddingBottom: 10, cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setSortDesc(d => !d)}
                            title={sortDesc ? "Newest first (click to reverse)" : "Oldest first (click to reverse)"}
                        >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                Changed At
                                {sortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />}
                            </span>
                        </th>
                        <th style={{ whiteSpace: 'nowrap', borderBottom: '2px solid #d1d5db', paddingTop: 10, paddingBottom: 10 }}>Changed By</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map((r, idx) => {
                        const prev = idx > 0 ? sortedRows[idx - 1] : null;
                        const isGroupStart = !prev || prev.changedAt !== r.changedAt || prev.source !== r.source || prev.changedById !== r.changedById;
                        const cellBase: React.CSSProperties = {
                            whiteSpace: 'nowrap',
                            borderBottom: '1px solid #f3f4f6',
                            paddingTop: 10,
                            paddingBottom: 10,
                            verticalAlign: 'middle',
                        };
                        const isSensitive = SENSITIVE_FIELDS.has(r.fieldName);
                        const muted = <span style={{ color: '#d1d5db' }}>·</span>;
                        return (
                            <tr key={r.id}>
                                <td style={{ ...cellBase, textTransform: 'uppercase', fontSize: 11, fontWeight: 600, color: '#6b7280' }}>
                                    {isGroupStart ? r.source : muted}
                                </td>
                                <td style={cellBase}>{r.fieldName}</td>
                                <td style={{ ...cellBase, whiteSpace: 'normal' }}>
                                    <ChangeCell oldValue={r.oldValue} newValue={r.newValue} sensitive={isSensitive} />
                                </td>
                                <td style={cellBase}>
                                    {isGroupStart ? new Date(r.changedAt).toLocaleString() : muted}
                                </td>
                                <td style={cellBase}>
                                    {isGroupStart ? (r.changedByEmail || <span style={{ color: '#9ca3af' }}>—</span>) : muted}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
};

const PROVINCE_OPTIONS = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
];

export const AddDistroAccountModal = ({ showModal, onHide }) => {
	const [formData, setFormData] = useState({
		name: "",
		mailingStreet1: "",
		mailingStreet2: "",
		mailingCity: "",
		mailingProv: "",
		mailingCountry: "",
		mailingPostalCode: "",
		phone: "",
		contactFirstName: "",
		contactLastName: "",
		email: "",
		bankTransitNo: "",
		bankAccountNo: "",
		bankNo: "",
		isRmd: false,
		isBdd: false,
		isJg: false
	});

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData({
			...formData,
			[name]: type === 'checkbox' ? checked : value
		});
	}

	const handleSubmit = async (e) => {
		e.preventDefault();

		try {
			await axios.post("/api/Advisors/createdistro", formData);
			toastr.success(`Successfully created firm ${formData.name}`);
			setFormData({
				name: "",
				mailingStreet1: "",
				mailingStreet2: "",
				mailingCity: "",
				mailingProv: "",
				mailingCountry: "",
				mailingPostalCode: "",
				phone: "",
				contactFirstName: "",
				contactLastName: "",
				email: "",
				bankTransitNo: "",
				bankAccountNo: "",
				bankNo: "",
				isRmd: false,
				isBdd: false,
				isJg: false
			});
			onHide();
		}
		catch (e) {
			toastr.error("Failed to create a firm", e)
		}
	}

	return (
		<Modal show={showModal} onHide={onHide} className="Account-Details-Modal">
			<Form as="form" onSubmit={handleSubmit}>
				<Modal.Header closeButton>
					<Modal.Title className="advisor-modal-title">Add Firm Account</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Card>
						<Form.Group>
							<Form.Label className="advisor-form-label">Firm Name<span style={{ color: 'red' }}>*</span></Form.Label>
							<Form.Control name="name" value={formData.name} onChange={handleChange} required />
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">Mailing Street 1<span style={{ color: 'red' }}>*</span></Form.Label>
								<Form.Control name="mailingStreet1" value={formData.mailingStreet1} onChange={handleChange} required />
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">Mailing Street 2</Form.Label>
								<Form.Control name="mailingStreet2" value={formData.mailingStreet2} onChange={handleChange} />
							</Col>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">Mailing City<span style={{ color: 'red' }}>*</span></Form.Label>
								<Form.Control name="mailingCity" value={formData.mailingCity} onChange={handleChange} required />
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">Mailing Province<span style={{ color: 'red' }}>*</span></Form.Label>
								<Form.Select name="mailingProv" value={formData?.mailingProv} onChange={handleChange} required>
									<option value="">Select Province</option>
									{PROVINCE_OPTIONS.map((prov) => (
										<option key={prov.value} value={prov.value}>{prov.label}</option>
									))}
								</Form.Select>
							</Col>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">Mailing Country</Form.Label>
								<Form.Control name="mailingCountry" value={formData.mailingCountry} onChange={handleChange} />
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">Mailing Postal Code<span style={{ color: 'red' }}>*</span></Form.Label>
								<Form.Control name="mailingPostalCode" value={formData.mailingPostalCode} onChange={handleChange} required />
							</Col>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">Contact First Name<span style={{ color: 'red' }}>*</span></Form.Label>
								<Form.Control name="contactFirstName" value={formData.contactFirstName} onChange={handleChange} required />
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">Contact Last Name<span style={{ color: 'red' }}>*</span></Form.Label>
								<Form.Control name="contactLastName" value={formData.contactLastName} onChange={handleChange} required />
							</Col>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">Phone</Form.Label>
								<Form.Control name="phone" value={formData.phone} onChange={handleChange} />
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">Email<span style={{ color: 'red' }}>*</span></Form.Label>
								<Form.Control name="email" type="email" value={formData.email} onChange={handleChange} required />
							</Col>
						</Form.Group>
					<Form.Group as={Row}>
						<Col>
							<Form.Label className="advisor-form-label">Bank Transit No<span style={{ color: 'red' }}>*</span></Form.Label>
							<Form.Control
							name="bankTransitNo"
							type="text"
							inputMode="numeric"
							pattern="\d*"
							maxLength={5}
							value={formData.bankTransitNo}
							onChange={handleChange}
							required
							/>
						</Col>
						<Col>
							<Form.Label className="advisor-form-label">Bank No<span style={{ color: 'red' }}>*</span></Form.Label>
							<Form.Control
							name="bankNo"
							type="text"
							inputMode="numeric"
							pattern="\d*"
							maxLength={3}
							value={formData.bankNo}
							onChange={handleChange}
							required
							/>
						</Col>
						<Col>
							<Form.Label className="advisor-form-label">Bank Account No<span style={{ color: 'red' }}>*</span></Form.Label>
							<Form.Control
							name="bankAccountNo"
							type="text"
							inputMode="numeric"
							pattern="\d*"
							maxLength={12}
							value={formData.bankAccountNo}
							onChange={handleChange}
							required
							/>
						</Col>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">Is RMD</Form.Label>
								<div className="form-check form-switch">
									<input type="checkbox" className="form-check-input" name="isRmd" checked={formData.isRmd} onChange={handleChange} />
								</div>
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">Is BDD</Form.Label>
								<div className="form-check form-switch">
									<input type="checkbox" className="form-check-input" name="isBdd" checked={formData.isBdd} onChange={handleChange} />
								</div>
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">Is JG</Form.Label>
								<div className="form-check form-switch">
									<input type="checkbox" className="form-check-input" name="isJg" checked={formData.isJg} onChange={handleChange} />
								</div>
							</Col>
						</Form.Group>
					</Card>
				</Modal.Body>
				<Modal.Footer>
					<Button type="submit" variant="primary">
						Submit
					</Button>
					<Button type="button" variant="secondary" onClick={onHide}>
						Close
					</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
};

export const DistroAccountDetailsModal = ({ showModal, onHide, selectedItem, directorRoles }) => {
	const [isEditable, setIsEditable] = useState(false);
	const [activeTab, setActiveTab] = useState<string>('details');
	const [message, setMessage] = useState(null);
	const [confirmed, setConfirmed] = useState(false);
	const [formData, setFormData] = useState({
		id: "",
		name: "",
		healthClaimAccountId: "",
		healthClaimDistroId: "",
		mailingStreet1: "",
		mailingStreet2: "",
		mailingCity: "",
		mailingProv: "",
		mailingCountry: "",
		mailingPostalCode: "",
		healthClaimEmail1: "",
		healthClaimEmail2: "",
		phone: "",
		contactPerson: "",
		email: "",
		bankTransitNo: "",
		bankAccountNo: "",
		bankNo: "",
		isActive: false,
		isRmd: false,
		isBdd: false,
		isJg: false
	});

	const resetFormData = () => {
		setFormData({
			id: selectedItem?.id,
			name: selectedItem?.name,
			healthClaimAccountId: selectedItem?.healthClaimAccountId,
			healthClaimDistroId: selectedItem?.healthClaimDistroId,
			mailingStreet1: selectedItem?.mailingStreet1,
			mailingStreet2: selectedItem?.mailingStreet2,
			mailingCity: selectedItem?.mailingCity,
			mailingProv: selectedItem?.mailingProv,
			mailingCountry: selectedItem?.mailingCountry,
			mailingPostalCode: selectedItem?.mailingPostalCode,
			healthClaimEmail1: selectedItem?.healthClaimEmail1,
			healthClaimEmail2: selectedItem?.healthClaimEmail2,
			phone: selectedItem?.phone,
			contactPerson: selectedItem?.contactPerson,
			email: selectedItem?.contactEmail,
			bankTransitNo: selectedItem?.bankTransitNo,
			bankAccountNo: selectedItem?.bankAccountNo,
			bankNo: selectedItem?.bankNo,
			isActive: selectedItem?.isActive,
			isRmd: selectedItem?.isRmd,
			isBdd: selectedItem?.isBdd,
			isJg: selectedItem?.isJg
		});
		setMessage(null);
		setConfirmed(false);
	}

	useEffect(() => {
		if (showModal && selectedItem) {
			setActiveTab('details');
			resetFormData();
		}
	}, [showModal]);

	const directorRole = directorRoles?.[String(selectedItem?.id ?? "").toLowerCase()];
	const rmdProvinces = directorRole?.rmd ?? [];
	const bddProvinces = directorRole?.bdd ?? [];

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData({
			...formData,
			[name]: type === 'checkbox' ? checked : value
		});
	}

	const toggleEdit = (e) => {
		e.preventDefault();
		setIsEditable(true);
	}

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			if (!confirmed && selectedItem?.isJg && !formData?.isJg) {
				const impact = await axios.get(`/api/Advisors/distro/${selectedItem.id}/jgimpact`);
				const affected = impact.data?.advisorsAffected ?? 0;

				if (affected > 0) {
					setConfirmed(true);
					setMessage(`Turning off JG will clear the RMD and BDD of ${affected} advisor${affected === 1 ? "" : "s"} under this distro. Submit again to confirm.`);
					return;
				}
			}

			await axios.post("/api/Advisors/updatedistro", formData);
			toastr.success(`Successfully updated firm ${selectedItem.name}`);
			setMessage(null);
			setConfirmed(false);
			setIsEditable(false);
		}
		catch (e) {
			const err = e as AxiosError;
			if (err.response) {
				toastr.error("Failed to update the firm.", err.response?.data)
			}
			resetFormData();
		}
	}

	if (!selectedItem) return null;

	return (
		<Modal show={showModal} onHide={onHide} className="Account-Details-Modal">
			<Form as="form" onSubmit={handleSubmit}>
				<Modal.Header closeButton>
					<Modal.Title className="advisor-modal-title">Update Firm Account Details</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'details')} className="mb-3">
						<Tab eventKey="details" title="Details">
					<Card>
						<Form.Group>
							<Form.Label className="advisor-form-label">
								Firm Name
							</Form.Label>
							<Form.Control
								name="name"
								value={formData?.name || ""}
								onChange={handleChange}
								required
								readOnly={!isEditable}
							/>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">
									Health Claim Account ID
								</Form.Label>
								<Form.Control
									name="healthClaimAccountId"
									value={formData?.healthClaimAccountId || ""}
									onChange={handleChange}
									readOnly
								/>
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">
									Health Claim Firm ID
								</Form.Label>
								<Form.Control
									name="healthClaimDistroId"
									value={formData?.healthClaimDistroId || ""}
									onChange={handleChange}
									readOnly
								/>
							</Col>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">
									Health Claim Email 1
								</Form.Label>
								<Form.Control
									name="healthClaimEmail1"
									value={formData?.healthClaimEmail1 || ""}
									onChange={handleChange}
									readOnly={!isEditable}
								/>
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">
									Health Claim Email 2
								</Form.Label>
								<Form.Control
									name="healthClaimEmail2"
									value={formData?.healthClaimEmail2 || ""}
									onChange={handleChange}
									readOnly={!isEditable}
								/>
							</Col>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">
									Mailing Street 1
								</Form.Label>
								<Form.Control
									name="mailingStreet1"
									value={formData?.mailingStreet1 || ""}
									onChange={handleChange}
									readOnly={!isEditable}
								/>
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">
									Mailing Street 2
								</Form.Label>
								<Form.Control
									name="mailingStreet2"
									value={formData?.mailingStreet2 || ""}
									onChange={handleChange}
									readOnly={!isEditable}
								/>
							</Col>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">
									Mailing City
								</Form.Label>
								<Form.Control
									name="mailingCity"
									value={formData?.mailingCity || ""}
									onChange={handleChange}
									readOnly={!isEditable}
								/>
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">
									Mailing Province
								</Form.Label>
								<Form.Control
									name="mailingProv"
									value={formData?.mailingProv || ""}
									onChange={handleChange}
									readOnly={!isEditable}
								/>
							</Col>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">
									Mailing Country
								</Form.Label>
								<Form.Control
									name="mailingCountry"
									value={formData?.mailingCountry || ""}
									onChange={handleChange}
									readOnly={!isEditable}
								/>
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">
									Mailing Postal Code
								</Form.Label>
								<Form.Control
									name="mailingPostalCode"
									value={formData?.mailingPostalCode || ""}
									onChange={handleChange}
									readOnly={!isEditable}
								/>
							</Col>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">
									Contact Person
								</Form.Label>
								<Form.Control
									name="contactPerson"
									value={formData?.contactPerson || ""}
									onChange={handleChange}
									readOnly={!isEditable}
								/>
							</Col>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">
									Phone
								</Form.Label>
								<Form.Control
									name="phone"
									value={formData?.phone || ""}
									onChange={handleChange}
									readOnly={!isEditable}
								/>
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">
									Email
								</Form.Label>
								<Form.Control
									name="email"
									value={formData?.email || ""}
									onChange={handleChange}
									readOnly={!isEditable}
								/>
							</Col>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">
									Bank Transit No
								</Form.Label>
								<Form.Control
									name="bankTransitNo"
									value={formData?.bankTransitNo || ""}
									onChange={handleChange}
									readOnly={!isEditable}
									inputMode="numeric"
									pattern="\d*"
									maxLength={5}
								/>
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">
									Bank No
								</Form.Label>
								<Form.Control
									name="bankNo"
									value={formData?.bankNo || ""}
									onChange={handleChange}
									readOnly={!isEditable}
									inputMode="numeric"
									pattern="\d*"
									maxLength={3}
								/>
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">
									Bank Account No
								</Form.Label>
								<Form.Control
									name="bankAccountNo"
									value={formData?.bankAccountNo || ""}
									onChange={handleChange}
									readOnly={!isEditable}
									inputMode="numeric"
									pattern="\d*"
									maxLength={12}
								/>
							</Col>
						</Form.Group>
						<Form.Group as={Row}>
							<Col>
								<Form.Label className="advisor-form-label">Is RMD</Form.Label>
								<div className="form-check form-switch">
									<input type="checkbox" className="form-check-input"
										disabled={!isEditable || rmdProvinces.length > 0}
										name="isRmd" checked={formData?.isRmd} onChange={handleChange} />
								</div>
								{rmdProvinces.length > 0 && (
									<Form.Text className="text-muted">
										Currently assigned as RMD for {rmdProvinces.join(", ")}. Please manage in the RMD/BDD Management tab
									</Form.Text>
								)}
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">Is BDD</Form.Label>
								<div className="form-check form-switch">
									<input type="checkbox" className="form-check-input"
										disabled={!isEditable || bddProvinces.length > 0}
										name="isBdd" checked={formData?.isBdd} onChange={handleChange} />
								</div>
								{bddProvinces.length > 0 && (
									<Form.Text className="text-muted">
										Currently assigned as BDD for {bddProvinces.join(", ")}. Please manage in the RMD/BDD Management tab
									</Form.Text>
								)}
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">Is JG</Form.Label>
								<div className="form-check form-switch">
									<input type="checkbox" className="form-check-input"
										disabled={!isEditable || rmdProvinces.length > 0 || bddProvinces.length > 0}
										name="isJg" checked={formData?.isJg} onChange={handleChange} />
								</div>
								{(rmdProvinces.length > 0 || bddProvinces.length > 0) && (
									<Form.Text className="text-muted">
										Currently assigned as a Province Director.
									</Form.Text>
								)}
							</Col>
							<Col>
								<Form.Label className="advisor-form-label">Is Active</Form.Label>
								<div className="form-check form-switch">
									<input type="checkbox" className="form-check-input"
										disabled={!isEditable}
										name="isActive" checked={formData?.isActive} onChange={handleChange} />
								</div>
							</Col>
						</Form.Group>
						{message && <Form.Text className="text-danger">{message}</Form.Text>}
					</Card>
					</Tab>
					<Tab eventKey="history" title="History">
						<DistroAuditLogTab distroAccountId={selectedItem?.id} active={activeTab === 'history'} />
					</Tab>
				</Tabs>
				</Modal.Body>
				<Modal.Footer>
					{activeTab === 'details' && (!isEditable ? (
						<Button variant="primary" type="button" onClick={toggleEdit}>
							Edit
						</Button>
					) : (
						<>
							<Button type="submit" variant="primary">
								Save
							</Button>
							<Button type="button" variant="secondary" onClick={() => { resetFormData(); setIsEditable(false); }}>
								Cancel
							</Button>
						</>
					))}
				</Modal.Footer>
			</Form>
		</Modal>
	);
}
